import { TranslateService } from '@ngx-translate/core';

export class NumberFormatterUtil {
  /**
   * Formats large numbers with appropriate units (thousand, million, billion, trillion)
   * @param value - The number to format
   * @param translateService - Translation service for localized unit names
   * @param decimalPlaces - Number of decimal places (default: 2)
   * @returns Formatted string with units
   */
  static formatNumberWithUnits(
    value: number,
    translateService: TranslateService,
    decimalPlaces: number = 2
  ): string {
    if (value >= 1000000000000) {
      // 12 zeros = trillions
      return (
        (value / 1000000000000).toFixed(decimalPlaces) +
        ' ' +
        translateService.instant('common.currency_trillion')
      );
    } else if (value >= 1000000000) {
      // 9 zeros = billions
      return (
        (value / 1000000000).toFixed(decimalPlaces) +
        ' ' +
        translateService.instant('common.currency_billion')
      );
    } else if (value >= 1000000) {
      // 6 zeros = millions
      return (
        (value / 1000000).toFixed(decimalPlaces) +
        ' ' +
        translateService.instant('common.currency_million')
      );
    } else if (value >= 1000) {
      // 3 zeros = thousands
      return (
        (value / 1000).toFixed(decimalPlaces) +
        ' ' +
        translateService.instant('common.currency_thousand')
      );
    }

    return value.toString();
  }

  /**
   * Formats numbers with standard suffixes (K, M, B, T)
   * @param value - The number to format
   * @param decimalPlaces - Number of decimal places (default: 1)
   * @returns Formatted string with short suffixes
   */
  static formatNumberWithSuffixes(value: number | string | null | undefined, decimalPlaces: number = 1): string {
    if (typeof value === 'string') {
      value = parseFloat(value);
    }
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    const absValue = Math.abs(value);
    const isNegative = value < 0;

    if (absValue >= 1000000000000) {
      // 12 zeros = trillions
      return (isNegative ? '-' : '') + (absValue / 1000000000000).toFixed(decimalPlaces) + 'T';
    } else if (absValue >= 1000000000) {
      // 9 zeros = billions
      return (isNegative ? '-' : '') + (absValue / 1000000000).toFixed(decimalPlaces) + 'B';
    } else if (absValue >= 1000000) {
      // 6 zeros = millions
      return (isNegative ? '-' : '') + (absValue / 1000000).toFixed(decimalPlaces) + 'M';
    } else if (absValue >= 1000) {
      // 3 zeros = thousands
      return (isNegative ? '-' : '') + (absValue / 1000).toFixed(decimalPlaces) + 'K';
    }

    return value.toString();
  }
}
